import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { DevPanelId } from '../../constants/devControls';

export type DevSectionId = DevPanelId | (string & {});

export type DevSectionTier = 'route' | 'global';

export interface DevSectionRegistration {
  id: DevSectionId;
  title: string;
  description?: string;
  /** Sort within the same tier; route-tier sections always appear above global. */
  order: number;
  tier: DevSectionTier;
  enabled: boolean;
  render: () => ReactNode;
}

interface DevControlsContextValue {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  registerSection: (section: DevSectionRegistration) => void;
  unregisterSection: (id: DevSectionId) => void;
  sections: DevSectionRegistration[];
}

const DevControlsContext = createContext<DevControlsContextValue | null>(null);

export function DevControlsProvider({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sectionsById, setSectionsById] = useState<Map<DevSectionId, DevSectionRegistration>>(
    () => new Map(),
  );

  const registerSection = useCallback((section: DevSectionRegistration) => {
    setSectionsById((prev) => {
      const next = new Map(prev);
      next.set(section.id, section);
      return next;
    });
  }, []);

  const unregisterSection = useCallback((id: DevSectionId) => {
    setSectionsById((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const sections = useMemo(
    () =>
      [...sectionsById.values()]
        .filter((section) => section.enabled)
        .sort((a, b) => {
          const tierRank = (tier: DevSectionTier) => (tier === 'route' ? 0 : 1);
          return (
            tierRank(a.tier) - tierRank(b.tier) ||
            a.order - b.order ||
            a.title.localeCompare(b.title)
          );
        }),
    [sectionsById],
  );

  const value = useMemo(
    () => ({ drawerOpen, setDrawerOpen, registerSection, unregisterSection, sections }),
    [drawerOpen, registerSection, unregisterSection, sections],
  );

  return <DevControlsContext.Provider value={value}>{children}</DevControlsContext.Provider>;
}

export function useDevControls() {
  const context = useContext(DevControlsContext);
  if (!context) {
    throw new Error('useDevControls must be used within DevControlsProvider');
  }
  return context;
}

export interface UseRegisterDevSectionOptions {
  id: DevSectionId;
  title: string;
  description?: string;
  order?: number;
  /** Route-mounted sections render above global static sections. */
  tier?: DevSectionTier;
  enabled: boolean;
  render: () => ReactNode;
}

/** Mount on a route to add a contextual section to the DEV drawer. */
export function useRegisterDevSection({
  id,
  title,
  description,
  order = 0,
  tier = 'route',
  enabled,
  render,
}: UseRegisterDevSectionOptions) {
  const { registerSection, unregisterSection } = useDevControls();
  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    if (!enabled) {
      unregisterSection(id);
      return;
    }

    registerSection({
      id,
      title,
      description,
      order,
      tier,
      enabled: true,
      render: () => renderRef.current(),
    });

    return () => unregisterSection(id);
  }, [description, enabled, id, order, registerSection, tier, title, unregisterSection]);
}
